import { useRef, type CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import {
  Button,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ArrowLeftIcon, ArrowRightLeftIcon, ExternalLinkIcon, PanelLeftIcon } from 'lucide-react';
import { IconRail, RailRow, useUserOrganizations } from '@/widgets/app-shell';
import { ENV } from '@/lib/config/env';
import { ROUTES } from '@/shared/config/routes';
import { motionTokens } from '@monksflow/monks-ui/preset';
import {
  isSectionActive,
  useOrganizationSettingsNav,
  type SettingsRailLink,
} from './useOrganizationSettingsNav';
import {
  settingsRailArea,
  useSettingsRailCollapsed,
  useSettingsRailStore,
} from './settings-rail.store';

/** The one expanded-sidebar width the app pins, the same figure `FeatureSidebar` uses. */
const SIDEBAR_WIDTH_PX = 280;

const TEST_ID = 'organization-settings-rail';

/**
 * One id for the whole rail, not one per `SidebarMenu`.
 *
 * The primitive scopes its own indicator per menu so two menus on a screen
 * cannot fight over it. Here the groups are one navigation with one selection,
 * so a per-group id would strand the indicator at a group boundary: moving
 * between two groups would fade rather than slide. Exactly one item is active
 * at a time, so a single id has a single claimant.
 */
const ACTIVE_INDICATOR_LAYOUT_ID = `${TEST_ID}-active-indicator`;

/**
 * The primitive's own `horizontal` active-indicator geometry, copied because
 * `asChild` is what suppresses it: the indicator cannot be injected around a
 * Slot without breaking Radix's single-child rule, so a consumer that needs a
 * real anchor has to draw it. Changing either of these means changing the
 * design system, not this line.
 */
const INDICATOR_CLASS = 'absolute inset-0 rounded-md bg-sidebar-accent';
const INDICATOR_CONTENT_CLASS = 'relative z-10 flex w-full items-center gap-1.5';

/**
 * The shared-element recipe the primitive uses for this indicator and the Tabs
 * one, so all three read as a single gesture.
 *
 * Read off Tessra's own motion tokens rather than copied: the package publishes
 * the same `--duration-*` / `--ease-*` scale as a JS mirror precisely because
 * motion cannot read the CSS properties, so a retune in the design system
 * reaches this indicator without anyone editing it.
 */
const INDICATOR_TRANSITION = {
  duration: motionTokens.duration.fast,
  ease: motionTokens.ease.inOut,
} as const;

const ORGANIZATION_BOX =
  'flex w-full items-center gap-xs rounded-lg border border-border px-xs py-xs';

/**
 * Where to send someone who wants to work in a different organization.
 *
 * The platform screen owns the choice, so this hands it only where to come
 * back to: naming an organization here would be guessing at a parameter that
 * screen has never been shown to read. It is the same link the app sidebar's
 * workspace menu has always offered.
 *
 * The redirect is the origin rather than the open URL, because org-scoped
 * resources do not exist in the organization being moved to.
 */
const switchOrganizationUrl = () =>
  `${ENV.PLATFORM_BASE_URL}/switch-organization` +
  `?redirectUri=${encodeURIComponent(window.location.origin)}`;

/**
 * The settings rail: every place an organization's settings can be changed,
 * grouped by who owns the setting, with the way back to the app underneath.
 *
 * It replaces a tab bar that sat inside the content area, so it is the only
 * thing on screen naming the open section — hence the descendant-based active
 * state in `isSectionActive` rather than the tab bar's strict equality.
 *
 * Collapsed, it becomes the app's own 75px icon rail with these destinations,
 * since it is the only navigation on screen and every destination has to stay
 * reachable. The agent builder starts collapsed, since its canvas wants the
 * width; each area remembers what the person last chose.
 *
 * It draws no surface of its own — hence `bg-transparent` over the primitive's
 * `bg-sidebar`: it sits directly on the page background, and the content column
 * beside it is the only card in the frame.
 */
export const OrganizationSettingsRail = () => {
  const { t } = useTranslation('admin');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const groups = useOrganizationSettingsNav();
  const area = settingsRailArea(pathname);
  const collapsed = useSettingsRailCollapsed(area);
  const setCollapsed = useSettingsRailStore((state) => state.setCollapsed);
  const label = t('admin.organizationSettings.settingsSections');
  // Each state renders its own tree, so the toggle clicked is gone once the rail changes. Set on a
  // click, read by the toggle that replaces it, so focus stays on the toggle across the swap.
  const toggled = useRef(false);
  const toggle = (next: boolean) => () => {
    toggled.current = true;
    setCollapsed(area, next);
  };
  const focusIfToggled = (button: HTMLButtonElement | null) => {
    if (!button || !toggled.current) return;
    toggled.current = false;
    button.focus();
  };

  if (collapsed) {
    const open = (item: SettingsRailLink) => () => {
      if (item.isExternal) window.open(item.href, '_blank', 'noopener,noreferrer');
      else void navigate(item.href);
    };
    return (
      <IconRail
        label={label}
        testId={TEST_ID}
        header={
          <>
            <RailOrganizationMark />
            <RailToggle collapsed onClick={toggle(false)} buttonRef={focusIfToggled} />
          </>
        }
        items={groups.flatMap((group) =>
          group.items.map((item) => ({
            id: item.id,
            label: item.label,
            ...(item.isExternal
              ? {
                  accessibleLabel: t('admin.organizationSettings.opensInNewTab', {
                    label: item.label,
                  }),
                }
              : {}),
            icon: null,
            IconComponent: item.IconComponent,
            active: !item.isExternal && isSectionActive(pathname, item.href),
            onClick: open(item),
          })),
        )}
        bottomItems={[
          {
            id: 'back',
            label: t('admin.organizationSettings.back'),
            icon: null,
            IconComponent: ArrowLeftIcon,
            onClick: () => void navigate(ROUTES.HOME),
          },
        ]}
      />
    );
  }

  // The wrapper is a full-viewport flex row by default; here it is one column
  // inside the settings frame, so it hands its height back to the layout.
  const providerStyle = {
    '--sidebar-width': `${String(SIDEBAR_WIDTH_PX)}px`,
    width: 'auto',
    minHeight: 0,
    height: '100%',
  } as CSSProperties;

  return (
    <SidebarProvider defaultOpen style={providerStyle}>
      <Sidebar
        collapsible="none"
        // The primitive is a plain div, which an `aria-label` alone does not
        // name — without this the rail is unreachable by landmark.
        role="navigation"
        aria-label={label}
        data-testid={TEST_ID}
        className="gap-sm bg-transparent px-xs py-sm border-r border-muted"
      >
        <SidebarHeader className="flex-row items-center gap-2xs p-0">
          <RailOrganization />
          <RailToggle collapsed={false} onClick={toggle(true)} buttonRef={focusIfToggled} />
        </SidebarHeader>

        <SidebarContent className="gap-xl p-0">
          {groups.map((group) => (
            <SidebarGroup key={group.id} className="gap-2xs p-0">
              <SidebarGroupLabel asChild>
                <h3>{group.heading}</h3>
              </SidebarGroupLabel>
              <SidebarMenu>
                {group.items.map((item) => (
                  <RailLink
                    key={item.id}
                    item={item}
                    isActive={isSectionActive(pathname, item.href)}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>

        {/* Home rather than history: settings is reached from a menu that is on
            every screen, so there is no one page it was opened from, and stepping
            back through the sections just visited is not leaving settings.
            A button rather than another row, because leaving settings is not one
            more destination in the list above it. */}
        <SidebarFooter className="p-0">
          <Button asChild variant="outline" className="w-full">
            <Link to={ROUTES.HOME} data-testid={`${TEST_ID}-back`}>
              <ArrowLeftIcon className="size-4 shrink-0" aria-hidden />
              {t('admin.organizationSettings.back')}
            </Link>
          </Button>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
};

/** The organization's mark alone, heading the collapsed rail. */
function RailOrganizationMark() {
  const { assignedOrganization } = useUserOrganizations();
  return (
    <RailRow>
      <span
        data-testid={`${TEST_ID}-organization`}
        title={assignedOrganization?.name}
        className="flex size-10 items-center justify-center"
      >
        {assignedOrganization && <OrganizationMark organization={assignedOrganization} />}
      </span>
    </RailRow>
  );
}

interface RailOrganizationLike {
  id: string;
  name: string;
  logoUrl?: string | null;
}

/**
 * Whose settings these are, at the top of the rail - and, for someone who
 * belongs to more than one organization, the way to the others.
 *
 * The box itself never becomes a control. The app cannot move a session
 * between organizations; only the platform screen can, so the affordance is a
 * link out to it rather than a list to choose from. A reader with a single
 * organization gets no link at all, which is what the sidebar's workspace menu
 * already does with this same flag - and what it does while the list is still
 * loading or after it failed, since neither can prove there is a choice.
 */
function RailOrganization() {
  const { t } = useTranslation('admin');
  const { assignedOrganization, hasMultipleOrganizations } = useUserOrganizations();

  if (!assignedOrganization) {
    return null;
  }

  return (
    <div data-testid={`${TEST_ID}-organization`} className={ORGANIZATION_BOX}>
      <OrganizationMark organization={assignedOrganization} />
      <span className="type-body-md-strong min-w-0 flex-1 truncate text-left text-foreground">
        {assignedOrganization.name}
      </span>

      {hasMultipleOrganizations && (
        // Its own provider, as every other redesigned surface with a tooltip
        // does: the delay this icon wants is not the app-wide default.
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={switchOrganizationUrl()}
                aria-label={t('admin.organizationSettings.switchOrganization')}
                data-testid={`${TEST_ID}-organization-switch`}
                className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast ease-out hover:bg-surface hover:text-foreground"
              >
                <ArrowRightLeftIcon className="size-3.5" aria-hidden />
              </a>
            </TooltipTrigger>
            <TooltipContent>{t('admin.organizationSettings.switchOrganization')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * The organization's logo, or its initial where it has none.
 *
 * Decorative either way: the name sits beside it, so reading it out would only
 * repeat what the row already says.
 */
function OrganizationMark({ organization }: { organization: RailOrganizationLike }) {
  if (organization.logoUrl) {
    return (
      <img src={organization.logoUrl} alt="" className="size-6 shrink-0 rounded-md object-cover" />
    );
  }

  return (
    <span
      aria-hidden
      className="type-label-xs flex size-6 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground"
    >
      {organization.name.charAt(0).toUpperCase()}
    </span>
  );
}

interface RailLinkProps {
  item: SettingsRailLink;
  isActive: boolean;
}

/**
 * One destination, as the primitive's menu button wrapping a router link.
 *
 * `asChild` is what makes the row a real anchor rather than a button that
 * navigates, so the browser's own open-in-new-tab and copy-link still work.
 * That is also what costs the primitive's sliding indicator, which it injects
 * only when it owns the element - so the row draws it, to the primitive's own
 * `horizontal` spec.
 *
 * `isActive` is deliberately NOT handed to the button while the animated
 * indicator is on screen. It would set `data-active`, and the static
 * `data-[active=true]` fill paints the moment React commits - the destination
 * would light up before the slide reached it. The primitive suppresses its own
 * `data-active` for the same reason. `aria-current` is set on the anchor
 * instead, so the indicator being a paint detail never costs the semantics.
 */
function RailLink({ item, isActive }: RailLinkProps) {
  const { t } = useTranslation('admin');
  const testId = `${TEST_ID}-item-${item.id}`;
  // `useReducedMotion` returns null before it has read the query; only a true
  // reading suppresses the slide.
  const prefersReducedMotion = useReducedMotion() ?? false;
  const slides = isActive && !prefersReducedMotion;

  const content = (
    <>
      {slides && (
        <motion.div
          data-slot="sidebar-active-indicator"
          data-testid={`${TEST_ID}-active-indicator`}
          layoutId={ACTIVE_INDICATOR_LAYOUT_ID}
          className={INDICATOR_CLASS}
          transition={INDICATOR_TRANSITION}
        />
      )}
      <span className={INDICATOR_CONTENT_CLASS}>
        <span className="flex shrink-0 items-center" aria-hidden>
          <item.IconComponent className="size-4" />
        </span>
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.isExternal && (
          <ExternalLinkIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        )}
      </span>
    </>
  );

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive && !slides}>
        {item.isExternal ? (
          // `noopener` is what keeps the opened tab from reaching back through
          // `window.opener`; `noreferrer` withholds the settings URL we came from.
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('admin.organizationSettings.opensInNewTab', { label: item.label })}
            data-testid={testId}
          >
            {content}
          </a>
        ) : (
          <Link to={item.href} aria-current={isActive ? 'page' : undefined} data-testid={testId}>
            {content}
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * Collapses the rail to the app's own 75px icon rail and back. At the top in both states, so it
 * never moves under the pointer of someone toggling it twice.
 */
function RailToggle({
  collapsed,
  onClick,
  buttonRef,
}: {
  collapsed: boolean;
  onClick: () => void;
  buttonRef: (button: HTMLButtonElement | null) => void;
}) {
  const { t } = useTranslation('admin');
  const label = collapsed
    ? t('admin.organizationSettings.expandRail')
    : t('admin.organizationSettings.collapseRail');
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-8 shrink-0 text-muted-foreground"
      aria-label={label}
      aria-expanded={!collapsed}
      title={label}
      data-testid={`${TEST_ID}-toggle`}
      ref={buttonRef}
      onClick={onClick}
    >
      <PanelLeftIcon className="size-4" aria-hidden />
    </Button>
  );
}
